import React from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { apiUrl } from "../lib/apiBase";
import { getAuthToken } from "../lib/auth";
import { isNativeStoreApp } from "../lib/appMode";

function currentSignedIn() {
  return Boolean(getAuthToken());
}

async function preparePhoto(file) {
  if (!file || !String(file.type || "").toLowerCase().startsWith("image/")) {
    throw new Error("Choose a photo from your device.");
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("That photo could not be opened."));
      img.src = objectUrl;
    });

    const sourceWidth = Math.max(1, Number(image.naturalWidth || image.width || 1));
    const sourceHeight = Math.max(1, Number(image.naturalHeight || image.height || 1));
    const maxSide = 2200;
    const scale = Math.min(1, maxSide / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Photo processing is unavailable on this device.");

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(image, 0, 0, width, height);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error("Photo processing failed."))),
        "image/jpeg",
        0.88
      );
    });

    return new File([blob], `ga-photo-${Date.now()}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function findProfileUploadButton() {
  return Array.from(document.querySelectorAll("#root button")).find((button) => {
    const text = String(button.textContent || "").trim().toLowerCase();
    return text === "upload gameplay" || text === "upload to feed";
  });
}

export default function NativePhotoComposer() {
  const location = useLocation();
  const fileRef = React.useRef(null);
  const [signedIn, setSignedIn] = React.useState(currentSignedIn);
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState("text");
  const [file, setFile] = React.useState(null);
  const [preview, setPreview] = React.useState("");
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState("");

  const path = String(location.pathname || "");
  const nativeApp = isNativeStoreApp();
  const visibleRoute = path === "/feed" || path === "/me";

  React.useEffect(() => {
    const refresh = () => setSignedIn(currentSignedIn());
    window.addEventListener("auth:changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("auth:changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  React.useEffect(() => {
    const openComposer = (event) => {
      const requestedMode = String(event?.detail?.mode || "text").toLowerCase();
      setMode(["text", "photo", "video"].includes(requestedMode) ? requestedMode : "text");
      setMessage("");
      setOpen(true);
    };
    window.addEventListener("ga:open-post-composer", openComposer);
    return () => window.removeEventListener("ga:open-post-composer", openComposer);
  }, []);

  React.useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  React.useEffect(() => {
    if (!open) return undefined;
    const prior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prior;
    };
  }, [open]);

  if (!signedIn || !visibleRoute) return null;

  const reset = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview("");
    setFile(null);
    setText("");
    setMessage("");
    setMode("text");
    if (fileRef.current) fileRef.current.value = "";
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
    reset();
  };

  const chooseFile = (selected) => {
    if (!selected) return;
    if (!String(selected.type || "").toLowerCase().startsWith("image/")) {
      setMessage("Choose a photo from your device.");
      return;
    }
    if (Number(selected.size || 0) > 25 * 1024 * 1024) {
      setMessage("That photo is too large. Choose one under 25 MB.");
      return;
    }

    if (preview) URL.revokeObjectURL(preview);
    setFile(selected);
    setPreview(URL.createObjectURL(selected));
    setMessage("");
  };

  const finishPost = (post) => {
    window.dispatchEvent(new CustomEvent("ga:feed-post-created", { detail: post || {} }));
    if (String(post?.mediaType || "") === "image" || post?.imageUrl) {
      window.dispatchEvent(new CustomEvent("ga:photo-post-created", { detail: post || {} }));
    }
    setOpen(false);
    reset();

    if (path === "/feed") {
      const target = post?.id ? `/feed?post=${encodeURIComponent(post.id)}` : "/feed";
      window.location.assign(target);
    }
  };

  const postText = async () => {
    const clean = String(text || "").trim();
    if (!clean || busy) return;
    const token = getAuthToken();
    if (!token) {
      setMessage("Sign in again before posting.");
      return;
    }

    setBusy(true);
    setMessage("Posting…");
    try {
      const response = await fetch(apiUrl("/posts/text"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: clean }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || "Could not publish this post.");
      }
      finishPost(data.post || {});
    } catch (error) {
      setMessage(error?.message || "Could not publish this post.");
    } finally {
      setBusy(false);
    }
  };

  const postPhoto = async () => {
    if (!file || busy) return;
    const token = getAuthToken();
    if (!token) {
      setMessage("Sign in again before posting.");
      return;
    }

    setBusy(true);
    setMessage("Preparing photo…");
    try {
      const prepared = await preparePhoto(file);
      const formData = new FormData();
      formData.append("image", prepared);
      formData.append("caption", String(text || "").trim());

      setMessage("Posting photo…");
      const response = await fetch(apiUrl("/posts/photo"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.ok) {
        throw new Error(data?.message || data?.error || "Could not post this photo.");
      }
      finishPost(data.post || {});
    } catch (error) {
      setMessage(error?.message || "Could not post this photo.");
    } finally {
      setBusy(false);
    }
  };

  const openVideoUploader = () => {
    setOpen(false);
    reset();

    if (path !== "/me") {
      window.location.assign("/me?upload=video");
      return;
    }

    const button = findProfileUploadButton();
    if (!button) {
      setMessage("Open your profile and choose Upload to Feed to add a video.");
      return;
    }
    button.dataset.gaAllowVideoUpload = "1";
    button.click();
  };

  const showLauncher = path === "/feed" || nativeApp;
  const maxLength = mode === "text" ? 500 : 240;
  const canSubmit = mode === "text" ? Boolean(String(text || "").trim()) : Boolean(file);

  return (
    <>
      {showLauncher ? (
        <button
          type="button"
          className="ga-photo-composer-launcher"
          onClick={() => {
            setMode("text");
            setOpen(true);
          }}
          aria-label="Create a Feed post"
          title="Create a Feed post"
        >
          <span aria-hidden="true">＋</span>
          <span className="ga-photo-composer-label">Post</span>
        </button>
      ) : null}

      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              className="ga-photo-composer-backdrop"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) close();
              }}
              role="presentation"
            >
              <section
                className="ga-photo-composer-sheet"
                role="dialog"
                aria-modal="true"
                aria-label="Create a Feed post"
              >
                <header>
                  <div>
                    <small>GAStreaming Feed</small>
                    <h2>Create a post</h2>
                  </div>
                  <button type="button" onClick={close} disabled={busy} aria-label="Close post composer">
                    ×
                  </button>
                </header>

                <div style={styles.tabs}>
                  {[
                    ["text", "Post"],
                    ["photo", "Photo"],
                    ["video", "Video"],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setMode(key);
                        setMessage("");
                      }}
                      style={mode === key ? styles.tabActive : styles.tab}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {mode === "video" ? (
                  <div style={styles.videoChoice}>
                    <div style={{ fontSize: 42 }}>🎥</div>
                    <strong>Upload a video to the Feed</strong>
                    <span>
                      Videos still use the reliable Mux uploader and remain limited to 2 minutes 30 seconds.
                    </span>
                    <button type="button" onClick={openVideoUploader} style={styles.primaryButton}>
                      Choose video
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => chooseFile(event.target.files?.[0])}
                    />

                    {mode === "photo" ? (
                      preview ? (
                        <button
                          type="button"
                          className="ga-photo-preview"
                          onClick={() => fileRef.current?.click()}
                          disabled={busy}
                          aria-label="Choose a different photo"
                        >
                          <img src={preview} alt="Selected post preview" />
                          <span>Choose another photo</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="ga-photo-pick"
                          onClick={() => fileRef.current?.click()}
                        >
                          <span aria-hidden="true">📷</span>
                          <strong>Choose a photo</strong>
                          <small>JPG, PNG, WebP, GIF, or a phone camera picture</small>
                        </button>
                      )
                    ) : null}

                    <label className="ga-photo-caption">
                      <span>{mode === "text" ? "What’s on your mind?" : "Caption"}</span>
                      <textarea
                        value={text}
                        onChange={(event) => setText(event.target.value.slice(0, maxLength))}
                        placeholder={
                          mode === "text"
                            ? "Share something with the Get Active community…"
                            : "Say something about this photo…"
                        }
                        rows={mode === "text" ? 6 : 3}
                        disabled={busy}
                      />
                      <small>{text.length}/{maxLength}</small>
                    </label>

                    {message ? <div className="ga-photo-message">{message}</div> : null}

                    <div className="ga-photo-actions">
                      <button type="button" className="secondary" onClick={close} disabled={busy}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="primary"
                        onClick={mode === "text" ? postText : postPhoto}
                        disabled={!canSubmit || busy}
                      >
                        {busy ? "Posting…" : mode === "text" ? "Post" : "Post photo"}
                      </button>
                    </div>
                  </>
                )}
              </section>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

const styles = {
  tabs: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    padding: "14px 18px 0",
  },
  tab: {
    minHeight: 42,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.12)",
    background: "rgba(255,255,255,.05)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  },
  tabActive: {
    minHeight: 42,
    borderRadius: 12,
    border: "1px solid rgba(255,65,95,.52)",
    background: "rgba(170,12,42,.34)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  },
  videoChoice: {
    margin: 18,
    minHeight: 250,
    display: "grid",
    placeItems: "center",
    alignContent: "center",
    gap: 10,
    textAlign: "center",
    padding: 24,
    borderRadius: 18,
    border: "1px dashed rgba(255,255,255,.2)",
    background: "rgba(255,255,255,.04)",
    color: "white",
  },
  primaryButton: {
    minHeight: 46,
    marginTop: 8,
    padding: "0 18px",
    borderRadius: 999,
    border: "1px solid rgba(255,65,95,.5)",
    background: "linear-gradient(135deg,#ff1744,#9d001f)",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  },
};